import { RefObject, useEffect, useState } from "react";

/**
 * useMenuPosition is a React hook that determines whether a dropdown menu should
 * be displayed below or above its trigger element, based on available viewport space.
 *
 * @param isOpen - Boolean indicating if the menu is open.
 * @param ref - RefObject of the trigger element (the element the menu is anchored to).
 * @param numItems - The number of items in the menu, used to estimate menu height.
 * @param itemHeight - The height (in pixels) of each menu item. Default is 40.
 * @param menuMaxHeight - The maximum height (in pixels) the menu can have. Default is 300.
 * @param menuPadding - The vertical padding (in pixels) to add to the menu height. Default is 16.
 * @returns {boolean} - Returns true if the menu should be displayed below (from top), false if above (from bottom).
 *
 * The hook recalculates on open state, content size, and trigger ref changes.
 * If there is not enough space below the trigger to fit the menu, but there is enough space above,
 * the menu will render above the trigger. Otherwise, it renders below.
 */
export function useMenuPosition(
  isOpen: boolean,
  ref: RefObject<HTMLElement>,
  numItems: number,
  itemHeight = 40,
  menuMaxHeight = 300,
  menuPadding = 16
) {
  const [showMenuFromTop, setShowMenuFromTop] = useState(true);

  useEffect(() => {
    if (isOpen && ref.current) {
      const rect = ref.current.getBoundingClientRect();
      const menuHeight = Math.min(
        menuMaxHeight,
        numItems * itemHeight + menuPadding
      );
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      if (spaceBelow < menuHeight && spaceAbove > menuHeight)
        setShowMenuFromTop(false);
      else setShowMenuFromTop(true);
    }
  }, [isOpen, numItems, itemHeight, menuMaxHeight, menuPadding, ref]);

  return showMenuFromTop;
}
